/**
 * Recarga automática cuando un archivo de la app (chunk) ya no existe.
 *
 * Después de un deploy, quien tenía la app abierta o cacheada por el service
 * worker pide archivos con el nombre viejo, que ya no existen: el import
 * dinámico falla y React muestra la pantalla de error. Recargar una vez trae
 * la versión nueva, así que se hace solo, sin que la persona vea nada.
 *
 * Guarda contra bucles: si ya se recargó hace menos de 30 s, no vuelve a hacerlo
 * (el error entonces es otro y se deja a la pantalla de error).
 */
const CLAVE = 'fom_chunk_reload_at';
const VENTANA_MS = 30_000;

const PATRONES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
];

export function esErrorDeChunk(razon: unknown): boolean {
  const texto =
    typeof razon === 'string'
      ? razon
      : razon && typeof razon === 'object'
        ? `${(razon as { name?: string }).name ?? ''} ${(razon as { message?: string }).message ?? ''}`
        : '';
  return PATRONES.some((p) => texto.includes(p));
}

function yaRecargoHaceUnMomento(): boolean {
  try {
    const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
    return Number.isFinite(ultima) && Date.now() - ultima < VENTANA_MS;
  } catch {
    return false;
  }
}

/** Limpia el service worker y las cachés viejas y recarga. Devuelve false si no recargó (ya lo hizo hace poco). */
export function recargarPorVersionNueva(): boolean {
  if (typeof window === 'undefined' || yaRecargoHaceUnMomento()) return false;
  try { sessionStorage.setItem(CLAVE, String(Date.now())); } catch { /* sin almacenamiento: se recarga igual */ }

  const recargar = () => window.location.reload();
  const limpiar = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
      }
      if ('caches' in window) {
        const claves = await caches.keys();
        await Promise.all(claves.map((k) => caches.delete(k)));
      }
    } catch { /* si falla la limpieza, igual se recarga */ }
  };
  // Tope de 2 s: la limpieza nunca debe dejar la pantalla colgada.
  void Promise.race([limpiar(), new Promise((r) => setTimeout(r, 2000))]).then(recargar);
  return true;
}

/** Engancha los eventos globales: se llama una vez, antes de montar React. */
export function instalarRecargaPorChunk(): void {
  if (typeof window === 'undefined') return;
  // Vite lo emite cuando falla la precarga de un import dinámico.
  window.addEventListener('vite:preloadError', (e) => {
    if (recargarPorVersionNueva()) e.preventDefault();
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (esErrorDeChunk(e.reason) && recargarPorVersionNueva()) e.preventDefault();
  });
  window.addEventListener('error', (e) => {
    if (esErrorDeChunk(e.error ?? e.message)) recargarPorVersionNueva();
  });
}
